import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DataMigration.css';

const DataMigration = () => {
  const navigate = useNavigate();
  const [migrationData, setMigrationData] = useState({
    students: [],
    lessons: [],
    notes: [],
    schedule: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);

  // Загружаем существующие данные при монтировании компонента
  useEffect(() => {
    loadExistingData();
  }, []);

  const loadExistingData = () => {
    const data = {
      students: [],
      lessons: [],
      notes: [],
      schedule: []
    };

    // Загружаем студентов
    try {
      const studentsData = localStorage.getItem('novas_students_data');
      const studentsDataAlt = localStorage.getItem('novas-students');
      
      if (studentsData) {
        const parsed = JSON.parse(studentsData);
        data.students = Object.values(parsed);
      } else if (studentsDataAlt) {
        const parsed = JSON.parse(studentsDataAlt);
        data.students = Object.values(parsed);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    }

    // Загружаем уроки
    try {
      const lessonsData = localStorage.getItem('novas_lessons_data');
      const lessonsDataAlt = localStorage.getItem('novas-lessons');
      
      if (lessonsData) {
        data.lessons = JSON.parse(lessonsData);
      } else if (lessonsDataAlt) {
        data.lessons = JSON.parse(lessonsDataAlt);
      }
    } catch (error) {
      console.error('Error loading lessons:', error);
    }

    // Загружаем заметки
    try {
      const notesData = localStorage.getItem('novas-notes');
      if (notesData) {
        data.notes = JSON.parse(notesData);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }

    // Загружаем расписание (может совпадать с уроками)
    try {
      const scheduleData = localStorage.getItem('novas-lessons');
      if (scheduleData) {
        data.schedule = JSON.parse(scheduleData);
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    }

    setMigrationData(data);
  };

  const handleMigrateData = async () => {
    setIsLoading(true);
    
    try {
      // Создаем уникальный ID для пользователя
      const userId = `user_${Date.now()}`;
      
      // Сохраняем данные с привязкой к пользователю
      // eslint-disable-next-line no-unused-vars
      const userData = {
        userId,
        migratedAt: new Date().toISOString(),
        data: migrationData
      };

      // Сохраняем в localStorage с префиксом пользователя
      localStorage.setItem(`novas_user_${userId}_students`, JSON.stringify(migrationData.students));
      localStorage.setItem(`novas_user_${userId}_lessons`, JSON.stringify(migrationData.lessons));
      localStorage.setItem(`novas_user_${userId}_notes`, JSON.stringify(migrationData.notes));
      localStorage.setItem(`novas_user_${userId}_schedule`, JSON.stringify(migrationData.schedule));
      
      // Сохраняем информацию о пользователе
      localStorage.setItem('novas_current_user', JSON.stringify({
        id: userId,
        isMigrated: true,
        migratedAt: new Date().toISOString()
      }));

      // Очищаем старые данные (опционально)
      // localStorage.removeItem('novas_students_data');
      // localStorage.removeItem('novas_lessons_data');
      // localStorage.removeItem('novas-notes');
      // localStorage.removeItem('novas-lessons');

      setMigrationComplete(true);
      
      // Перенаправляем на главную через 2 секунды
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Migration error:', error);
      alert('Ошибка при миграции данных. Попробуйте еще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipMigration = () => {
    // Создаем пустого пользователя без миграции
    const userId = `user_${Date.now()}`;
    localStorage.setItem('novas_current_user', JSON.stringify({
      id: userId,
      isMigrated: false,
      createdAt: new Date().toISOString()
    }));
    
    navigate('/');
  };

  return (
    <div className="migration-page">
      <div className="migration-container">
        <div className="migration-card">
          <div className="migration-header">
            <h1>Добро пожаловать в NOVAS 2.0!</h1>
            <p>Мы обнаружили данные в вашем браузере. Хотите ли вы перенести их в вашу новую учетную запись?</p>
          </div>

          <div className="data-summary">
            <h3>Найденные данные:</h3>
            <div className="data-items">
              <div className="data-item">
                <span className="data-icon">👥</span>
                <span className="data-label">Студенты:</span>
                <span className="data-count">{migrationData.students.length}</span>
              </div>
              <div className="data-item">
                <span className="data-icon">📚</span>
                <span className="data-label">Уроки:</span>
                <span className="data-count">{migrationData.lessons.length}</span>
              </div>
              <div className="data-item">
                <span className="data-icon">📝</span>
                <span className="data-label">Заметки:</span>
                <span className="data-count">{migrationData.notes.length}</span>
              </div>
              <div className="data-item">
                <span className="data-icon">📅</span>
                <span className="data-label">Расписание:</span>
                <span className="data-count">{migrationData.schedule.length}</span>
              </div>
            </div>
          </div>

          {migrationData.students.length > 0 && (
            <div className="students-preview">
              <h4>Ваши студенты:</h4>
              <div className="students-list">
                {migrationData.students.slice(0, 3).map((student, index) => (
                  <div key={index} className="student-preview">
                    <span className="student-name">{student.name}</span>
                    <span className="student-level">{student.proficiency}</span>
                  </div>
                ))}
                {migrationData.students.length > 3 && (
                  <div className="more-students">
                    +{migrationData.students.length - 3} еще
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="migration-actions">
            <button 
              className="migrate-button"
              onClick={handleMigrateData}
              disabled={isLoading}
            >
              {isLoading ? 'Перенос данных...' : 'Перенести данные'}
            </button>
            
            <button 
              className="skip-button"
              onClick={handleSkipMigration}
              disabled={isLoading}
            >
              Начать с чистого листа
            </button>
          </div>

          {migrationComplete && (
            <div className="migration-success">
              <div className="success-icon">✅</div>
              <h3>Данные успешно перенесены!</h3>
              <p>Перенаправляем вас на главную страницу...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataMigration; 